import { timing, variantForLevel, } from './params';
/** Hash LCG déterministe à partir d'un seed entier. */
function lcg(seed) {
    const s = (Math.imul(seed | 0, 1664525) + 1013904223) >>> 0;
    return s / 0x100000000;
}
export function makeStimulus(trialIdx, durationMs, level) {
    const variant = variantForLevel(level);
    const r1 = lcg(trialIdx * 7 + 13);
    const r2 = lcg(trialIdx * 31 + 17);
    const r3 = lcg(trialIdx * 53 + 23);
    const shape = variant.shapes[Math.floor(r1 * variant.shapes.length)];
    const rotationDeg = variant.rotations[Math.floor(r2 * variant.rotations.length)];
    const mirrored = r3 < variant.mirrorRatio;
    return {
        trialIdx,
        durationMs: Math.max(150, durationMs),
        shape,
        rotationDeg,
        mirrored,
    };
}
export function createMindsEyeAdapter(level = 1) {
    let state = {
        subPhase: 'idle',
        stim: null,
        feedback: null,
        level,
        variant: variantForLevel(level),
    };
    const listeners = new Set();
    let resolver = null;
    let rejecter = null;
    let trialStart = 0;
    const timers = [];
    function clearTimers() {
        while (timers.length > 0) {
            const t = timers.pop();
            if (t !== undefined)
                clearTimeout(t);
        }
    }
    function update(patch) {
        state = { ...state, ...patch };
        for (const l of listeners) {
            try {
                l(state);
            }
            catch {
                /* ignored */
            }
        }
    }
    function settle(answer, stim) {
        if (!resolver)
            return;
        const rt = Date.now() - trialStart;
        const correct = (answer === 'mirror') === stim.mirrored;
        const r = resolver;
        resolver = null;
        rejecter = null;
        r({ answer, reactionTimeMs: rt, correct });
    }
    return {
        mount() {
            // Idempotent.
        },
        unmount() {
            clearTimers();
            listeners.clear();
            if (rejecter) {
                rejecter(new Error('unmount'));
                rejecter = null;
                resolver = null;
            }
        },
        presentStimulus(stim) {
            clearTimers();
            return new Promise((resolve, reject) => {
                resolver = resolve;
                rejecter = reject;
                update({ subPhase: 'fixation', stim, feedback: null });
                timers.push(setTimeout(() => {
                    update({ subPhase: 'reference' });
                    timers.push(setTimeout(() => {
                        update({ subPhase: 'gap' });
                        timers.push(setTimeout(() => {
                            update({ subPhase: 'comparison' });
                            trialStart = Date.now();
                            timers.push(setTimeout(() => {
                                update({ subPhase: 'awaiting_input' });
                                timers.push(setTimeout(() => {
                                    if (!resolver)
                                        return;
                                    const r = resolver;
                                    resolver = null;
                                    rejecter = null;
                                    // Pas de réponse : fallback "identical" et incorrect si mirror.
                                    const fallback = stim.mirrored
                                        ? 'identical'
                                        : 'mirror';
                                    r({
                                        answer: fallback,
                                        reactionTimeMs: timing.responseWindowMs,
                                        correct: false,
                                    });
                                }, timing.responseWindowMs));
                            }, stim.durationMs));
                        }, timing.gapMs));
                    }, stim.durationMs));
                }, timing.fixationMs));
            });
        },
        async showFeedback(correct) {
            update({
                subPhase: 'feedback',
                feedback: correct ? 'correct' : 'incorrect',
            });
            await new Promise((resolve) => {
                timers.push(setTimeout(() => {
                    update({ subPhase: 'iti' });
                    resolve();
                }, timing.feedbackMs));
            });
        },
        pause() {
            // Sprint 0.5 : no-op.
        },
        resume() {
            // Sprint 0.5 : no-op.
        },
        abort() {
            clearTimers();
            if (rejecter) {
                rejecter(new Error('aborted'));
                rejecter = null;
                resolver = null;
            }
        },
        getState() {
            return state;
        },
        subscribe(listener) {
            listeners.add(listener);
            listener(state);
            return () => {
                listeners.delete(listener);
            };
        },
        handleAnswer(answer) {
            if (state.subPhase !== 'awaiting_input' &&
                state.subPhase !== 'comparison') {
                return;
            }
            const stim = state.stim;
            if (!stim)
                return;
            clearTimers();
            settle(answer, stim);
        },
        setLevel(newLevel) {
            const v = variantForLevel(newLevel);
            update({ level: newLevel, variant: v });
        },
    };
}
//# sourceMappingURL=adapter.js.map